// Friendly, approximate "time ago" formatting (e.g. "3 hrs ago").
// Intentionally coarse — gives the user a sense of timeframe, not exact precision.

const MIN = 60_000;
const HR = 60 * MIN;
const DAY = 24 * HR;
const WK = 7 * DAY;
const MO = 30 * DAY;
const YR = 365 * DAY;

export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = now - then;
  const abs = Math.abs(diff);
  const dir = diff >= 0 ? "ago" : "from now";

  const unit = (n: number, label: string) => `${n} ${label}${n === 1 ? "" : "s"} ${dir}`;

  if (abs < 45 * 1000) return diff >= 0 ? "just now" : "in a moment";
  if (abs < HR) return unit(Math.round(abs / MIN), "min");
  if (abs < DAY) return unit(Math.round(abs / HR), "hr");
  if (abs < WK) return unit(Math.round(abs / DAY), "day");
  if (abs < MO) return unit(Math.round(abs / WK), "wk");
  if (abs < YR) return unit(Math.round(abs / MO), "mo");
  return unit(Math.round(abs / YR), "yr");
}

// Coarser, calendar-aware label for a day grouping header on the Logs page.
// "Today" / "Yesterday" for the two most recent days, otherwise a friendly
// "N days/weeks/months ago". Compares whole calendar days in local time so the
// label flips at midnight, not on a rolling 24h window.
export function dayRelativeLabel(iso: string, now: number = Date.now()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(new Date(now)) - startOf(then)) / DAY);

  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days === -1) return "Tomorrow";
  const ahead = days < 0;
  const n = Math.abs(days);
  const dir = ahead ? "from now" : "ago";
  if (n < 7) return `${n} days ${dir}`;
  if (n < 30) return `${Math.round(n / 7)} wk ${dir}`;
  if (n < 365) return `${Math.round(n / 30)} mo ${dir}`;
  return `${Math.round(n / 365)} yr ${dir}`;
}

export const ONE_DAY_MS = DAY;
